import { injectable, interfaces } from 'inversify';
import { action, IObservableArray, makeObservable, observable } from 'mobx';
import { AnnotationStatus } from '../annotation/annotations.interface';
import { IQuestionModel } from '../annotation/question.model';
import { ImageSetType } from '../projectDetails/sub/images/projectDetailsImages.model';
import { ProjectStatus } from '../projects/projects.model';
import { IFreeAccessPaging } from './freeAccessApi.service';

export enum AnnotateStage {
  NOTANNOTATED = 'NotAnnotated',
  ANNOTATED = 'Annotated',
  DRAFT = 'Draft',
  REVIEWED = 'Reviewed',
  REJECTED = 'Rejected',
}

export enum FreeAccessMode {
  VIEW = 'View',
  ANNOTATION = 'Annotation',
  REVIEW = 'Review',
  REVIEWCORRECT = 'ReviewCorrect',
  ANNOTATIONRESET = 'AnnotationReset',
  CORRECT = 'Correct',
}

export interface IFreeAccessStore {
  showImageDeletedMessage: boolean;
  index: number;
  total: number;
  previousId: string;
  nextId: string;
  projectId: string;
  projectStatus: ProjectStatus;
  imageId: string;
  annotatedByName: string;
  reviewedByName: string;
  lockedByName: string;
  activePreloads: IObservableArray<string>;
  paging: IFreeAccessPaging;
  freeAccessMode: FreeAccessMode;
  questions: IObservableArray<IQuestionModel>;
  annotateStage: AnnotateStage;
  status?: AnnotationStatus;
  rejectionReason?: string;
  isAssignedToCurrentUser: boolean;
  isAssigned: boolean;
  isImported: boolean;
  isWaitingForReviewAfterReject: boolean;
  imageSet: ImageSetType;

  setView(freeAccessMode: FreeAccessMode, annotateStage?: AnnotateStage): void;
}

export namespace IFreeAccessStore {
  export const $: interfaces.ServiceIdentifier<IFreeAccessStore> = Symbol('FREE_ACCESS_STORE');
}

@injectable()
export class FreeAccessStore implements IFreeAccessStore {
  rejectionReason?: string | undefined;

  activePreloads: IObservableArray<string> = observable.array([]);

  projectId: string = '';

  projectStatus: ProjectStatus = ProjectStatus.Draft;

  imageId: string = '';

  previousId: string = '';

  nextId: string = '';

  total: number = 0;

  index: number = 0;

  annotatedByName = '';

  reviewedByName = '';

  lockedByName = '';

  isAssignedToCurrentUser = false;

  isAssigned = false;

  freeAccessMode = FreeAccessMode.VIEW;

  isWaitingForReviewAfterReject: boolean = false;

  paging: IFreeAccessPaging = {
    wasChanged: false,
    checkDate: '',
    items: [],
  };

  questions: IObservableArray<IQuestionModel> = observable.array([]);

  annotateStage: AnnotateStage = AnnotateStage.NOTANNOTATED;

  showImageDeletedMessage: boolean = false;

  status = undefined;

  isImported = false;

  imageSet = ImageSetType.NotSet;

  constructor() {
    makeObservable(this, {
      projectId: observable,
      projectStatus: observable,
      imageId: observable,
      previousId: observable,
      nextId: observable,
      total: observable,
      index: observable,
      annotatedByName: observable,
      reviewedByName: observable,
      lockedByName: observable,
      isAssignedToCurrentUser: observable,
      isAssigned: observable,
      freeAccessMode: observable,
      isWaitingForReviewAfterReject: observable,
      paging: observable,
      questions: observable,
      annotateStage: observable,
      showImageDeletedMessage: observable,
      status: observable,
      isImported: observable,
      imageSet: observable,
      setView: action,
    });
  }

  setView(freeAccessMode: FreeAccessMode, annotateStage?: AnnotateStage): void {
    this.freeAccessMode = freeAccessMode;
    if (annotateStage) {
      this.annotateStage = annotateStage;
    }
  }
}
